import { useState, useEffect } from "react";
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";
import BaseButtonPrimary from "../BaseButtonPrimary";
import BaseButtonSecondary from "../BaseButtonSecondary";
import BaseInputText from "../BaseInputText";
import BaseInputTextArea from "../BaseInputTextArea";
import { ExistingProject } from "../../models/project";
import LoadingClipboardAnimation from "../LoadingClipBoardAnimation";
import BaseModal from "../BaseModal";

interface CreateProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  setTitle: (value: string) => void;
  description: string;
  setDescription: (value: string) => void;
  onCreateProject: () => void;
  isSubmitting: boolean;
  existingProjects: ExistingProject[];
  loadingProjects: boolean;
  onAddToProject?: (projectId: string) => Promise<void>;
}

export default function CreateProjectDialog({
  isOpen,
  onClose,
  title,
  setTitle,
  description,
  setDescription,
  onCreateProject,
  isSubmitting,
  existingProjects,
  loadingProjects,
  onAddToProject,
}: CreateProjectDialogProps): JSX.Element {
  const [mode, setMode] = useState<"create" | "add">("create");
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    if (isOpen) {
      setMode("create");
      setSelectedProjectId("");
      setSearchQuery("");
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (mode === "create") {
      onCreateProject();
    } else if (mode === "add" && onAddToProject && selectedProjectId) {
      onAddToProject(selectedProjectId);
    }
  };

  const isSubmitDisabled =
    isSubmitting ||
    (mode === "create" && !title) ||
    (mode === "add" && !selectedProjectId);

  // Filter projects based on search query
  const filteredProjects = existingProjects.filter(
    (project) =>
      project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (project.description &&
        project.description.toLowerCase().includes(searchQuery.toLowerCase())),
  );

  const header = (
    <div className="mb-4 flex w-full items-center justify-between rounded-t-lg bg-primary p-8 text-left text-white">
      <h1 className="inline-flex items-center text-xl">Project Options</h1>
      <button type="button" onClick={onClose}>
        <XMarkIcon
          aria-label="close project dialog"
          className="h-6 text-white"
        />
      </button>
    </div>
  );

  return (
    <BaseModal
      closeModal={onClose}
      open={isOpen}
      title={header}
      allowOverflowY={true}
      parentDivStyles="inline-block transform overflow-hidden max-w-lg rounded-lg bg-white text-left align-middle shadow-xl transition-all"
    >
      <div className="px-6 pb-6">
        <div className="mt-2">
          <label className="mb-2 block text-sm font-medium text-gray-700">
            What would you like to do?
          </label>
          <div className="space-y-3">
            <div className="flex items-start">
              <input
                id="create-project"
                name="project-option"
                type="radio"
                checked={mode === "create"}
                onChange={() => setMode("create")}
                className="mt-1 h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label
                htmlFor="create-project"
                className="ml-3 block cursor-pointer text-sm font-medium text-gray-700"
              >
                Create a new project
              </label>
            </div>
            <div className="flex items-start">
              <input
                id="add-to-project"
                name="project-option"
                type="radio"
                checked={mode === "add"}
                onChange={() => setMode("add")}
                className="mt-1 h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label
                htmlFor="add-to-project"
                className="ml-3 block cursor-pointer text-sm font-medium text-gray-700"
              >
                Add to an existing project
              </label>
            </div>
          </div>
        </div>

        {mode === "create" ? (
          <div className="mt-6 space-y-4">
            <p className="text-sm text-gray-500">
              Create a new project and add this job to it. You can add more jobs
              to this project later.
            </p>
            <BaseInputText
              admin={true}
              inputName="project-title"
              text="Project Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter project title"
              required
            />
            <BaseInputTextArea
              admin={true}
              inputName="project-description"
              text="Description"
              value={description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setDescription(e.target.value)
              }
              placeholder="Enter project description (optional)"
              rows={4}
            />
          </div>
        ) : (
          <div className="mt-6">
            <p className="mb-4 text-sm text-gray-500">
              Select an existing project to add this job to.
            </p>

            {loadingProjects ? (
              <div className="flex justify-center py-4">
                <LoadingClipboardAnimation />
              </div>
            ) : existingProjects.length === 0 ? (
              <div className="py-4 text-center text-gray-500">
                No projects found. Create a new project instead.
              </div>
            ) : (
              <>
                <div className="relative mb-3">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <div className="text-gray-400" aria-hidden="true">
                      🔍
                    </div>
                  </div>
                  <input
                    type="text"
                    className="block w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                    placeholder="Search projects..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="max-h-60 divide-y overflow-y-auto rounded-md border">
                  {filteredProjects.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      No projects match your search
                    </div>
                  ) : (
                    filteredProjects.map((project) => (
                      <div
                        key={project.id}
                        className={`flex cursor-pointer items-center p-3 hover:bg-gray-50 ${
                          selectedProjectId === project.id
                            ? "border-l-4 border-green-500 bg-green-50"
                            : ""
                        }`}
                        onClick={() => setSelectedProjectId(project.id)}
                      >
                        <div className="flex-grow">
                          <div className="font-medium">{project.title}</div>
                          {project.description && (
                            <div className="truncate text-sm text-gray-500">
                              {project.description}
                            </div>
                          )}
                        </div>
                        {selectedProjectId === project.id && (
                          <div className="h-6 w-6 flex-shrink-0 text-xl font-bold text-green-500">
                            ✓
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        )}

        <div className="mt-8 flex justify-end gap-3">
          <BaseButtonSecondary type="button" onClick={onClose}>
            Cancel
          </BaseButtonSecondary>
          <BaseButtonPrimary
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            isBusy={isSubmitting}
            busyText={mode === "create" ? "Creating..." : "Adding..."}
          >
            {mode === "create" ? "Create Project" : "Add to Project"}
          </BaseButtonPrimary>
        </div>
      </div>
    </BaseModal>
  );
}
